import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "./config";

const PRODUCTS_COLLECTION = "products";

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

// ─── Upload image to Cloudinary ───────────────────────────────────────────────
export const uploadProductImage = async (file, productName) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("upload_preset", UPLOAD_PRESET);
  formData.append("folder", "products");
  formData.append(
    "public_id",
    `${productName.replace(/\s+/g, "_").toLowerCase()}_${Date.now()}`,
  );

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    { method: "POST", body: formData },
  );

  if (!res.ok) throw new Error("Cloudinary upload failed");

  const data = await res.json();
  return { imageUrl: data.secure_url, publicId: data.public_id };
};

// ─── Fetch all active products (for shop) ────────────────────────────────────
export const getActiveProducts = async () => {
  const q = query(
    collection(db, PRODUCTS_COLLECTION),
    where("active", "==", true),
    orderBy("createdAt", "desc"),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// ─── Fetch all products (for admin) ──────────────────────────────────────────
export const getAllProducts = async () => {
  const q = query(
    collection(db, PRODUCTS_COLLECTION),
    orderBy("createdAt", "desc"),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// ─── Fetch products by category ───────────────────────────────────────────────
export const getProductsByCategory = async (category) => {
  const q = query(
    collection(db, PRODUCTS_COLLECTION),
    where("category", "==", category),
    where("active", "==", true),
    orderBy("createdAt", "desc"),
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
};

// ─── Fetch single product ─────────────────────────────────────────────────────
export const getProductById = async (id) => {
  const docRef = doc(db, PRODUCTS_COLLECTION, id);
  const docSnap = await getDoc(docRef);
  if (docSnap.exists()) {
    return { id: docSnap.id, ...docSnap.data() };
  }
  return null;
};

// ─── Add new product ──────────────────────────────────────────────────────────
export const addProduct = async (productData, imageFile) => {
  let imageUrl = "";
  let publicId = "";

  if (imageFile) {
    const result = await uploadProductImage(imageFile, productData.name);
    imageUrl = result.imageUrl;
    publicId = result.publicId;
  }

  const docRef = await addDoc(collection(db, PRODUCTS_COLLECTION), {
    ...productData,
    imageUrl,
    publicId,
    active: true,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return docRef.id;
};

// ─── Update existing product ──────────────────────────────────────────────────
export const updateProduct = async (id, productData, imageFile) => {
  const docRef = doc(db, PRODUCTS_COLLECTION, id);

  let updateData = {
    ...productData,
    updatedAt: serverTimestamp(),
  };

  if (imageFile) {
    const result = await uploadProductImage(imageFile, productData.name);
    updateData.imageUrl = result.imageUrl;
    updateData.publicId = result.publicId;
  }

  await updateDoc(docRef, updateData);
};

// ─── Toggle product active/inactive ──────────────────────────────────────────
export const toggleProductActive = async (id, currentStatus) => {
  const docRef = doc(db, PRODUCTS_COLLECTION, id);
  await updateDoc(docRef, {
    active: !currentStatus,
    updatedAt: serverTimestamp(),
  });
};

// ─── Delete product ───────────────────────────────────────────────────────────
export const deleteProduct = async (id) => {
  await deleteDoc(doc(db, PRODUCTS_COLLECTION, id));
};
